import { cn } from "@faire/prototype-playground/lib/utils";
import { useState } from "react";

import Button from "../design-system/Button";
import { ExportTextButton } from "../design-system/ExportTextButton";
import FilterButton from "../design-system/FilterButton";
import Search from "../design-system/Search";

export interface QuickFilter {
  label: string;
  Icon?: React.FC<any>;
}

export interface SlateTableActionsProps {
  /** Search input placeholder text */
  searchPlaceholder?: string;
  /** Quick filter pills shown after Sort and Filters (e.g. "Selling fast", "New arrivals") */
  quickFilters?: QuickFilter[];
  /** Label for the primary action button on the right */
  actionLabel?: string;
  /** Hide the search input */
  hideSearch?: boolean;
  /** Hide the sort button */
  hideSort?: boolean;
  /** Hide the filters button */
  hideFilters?: boolean;
  /** Hide the export icon button */
  hideExport?: boolean;
  /** Hide the primary action button */
  hideAction?: boolean;
  /** Additional className */
  className?: string;
}

const SortIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M4 6l4-4 4 4M4 10l4 4 4-4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function SlateTableActions({
  searchPlaceholder = "Search...",
  quickFilters = [],
  actionLabel = "Bulk edit",
  hideSearch = false,
  hideSort = false,
  hideFilters = false,
  hideExport = false,
  hideAction = false,
  className = "",
}: SlateTableActionsProps) {
  const [search, setSearch] = useState("");

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="flex items-center gap-2">
        {!hideSearch && (
          <Search
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            className="w-[220px] [&_input]:rounded-full"
          />
        )}
        {!hideSort && <FilterButton label="Sort" Icon={SortIcon} />}
        {!hideFilters && <FilterButton label="Filters" Icon={FilterIcon} />}
        {quickFilters.map((filter) => (
          <FilterButton
            key={filter.label}
            label={filter.label}
            Icon={filter.Icon}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        {!hideExport && (
          <ExportTextButton labelClassName="text-[13px] leading-4" />
        )}
        {!hideAction && (
          <Button variant="secondary" size="small">
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

// Filter Icon SVG (16px × 16px)
const FilterIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-inherit"
  >
    <path
      d="M2 4H14M4 8H12M6 12H10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
